import React from 'react';
import { t } from 'i18next';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'default' | 'warning' | 'danger';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = t('confirm') || 'Confirmar',
  cancelText = t('cancel') || 'Cancelar',
  onConfirm,
  onCancel,
  type = 'default',
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'warning':
        return {
          confirmButton: 'bg-yellow-500 hover:bg-yellow-600',
          titleColor: 'text-yellow-600',
          icon: '⚠️',
        };
      case 'danger':
        return {
          confirmButton: 'bg-red-500 hover:bg-red-600',
          titleColor: 'text-red-600',
          icon: '🗑️',
        };
      default:
        return {
          confirmButton: 'bg-blue-500 hover:bg-blue-600',
          titleColor: 'text-blue-600',
          icon: '❓',
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">{typeStyles.icon}</span>
          <h3 className={`text-lg font-semibold ${typeStyles.titleColor}`}>
            {title}
          </h3>
        </div>
        
        {/* Message */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {message}
        </p>
        
        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-white rounded-md font-medium transition-colors ${typeStyles.confirmButton}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;